import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";
import OfferCodePrompt from "../Component/OfferCodePrompt";
import ToastHolder from "../Component/Toast/ToastHolder";
import useIsMobile from "../Hook/mobile";
import Blocked from "../Page/Blocked";
import ManageMySub from "../Page/ManageMySub";
import MyEpheme from "../Page/MyEpheme";
import Search from "../Page/Search";
import PopinsHolder from "../Popins";
import Loader from "../Component/Loader";
import { displayPopin } from "../Hook/popins";
import useUser from "../Hook/user";
import ForgotPassword from "../Page/ForgotPassword";
const Home = React.lazy(() => import("../Page/Home"));
const EphemeUploader = React.lazy(() => import("../Page/EphemeUploader"));
const Profile = React.lazy(loadProfile);
const Members = React.lazy(() => import("../Page/Members"));
const Chat = React.lazy(() => import("../Page/Chat"));
const Tokens = React.lazy(loadTokens);
const Chats = React.lazy(() => import("../Page/Chats"));
const Challenge = React.lazy(() => import("../Page/Challenge"));
const Webcam = React.lazy(() => import("../Page/Webcam"));
const SingleChallenge = React.lazy(() => import("../Page/Challenge/SingleChallenge"));
const StaticPage = React.lazy(() => import("../Page/StaticPage"));
const ParametersPage = React.lazy(loadParameters);
const SuppressionPage = React.lazy(() => import("../Page/Suppression"));
const ResetPage = React.lazy(() => import("../Page/ResetPassword"));
const ContactPage = React.lazy(() => import("../Page/Contact"));
const InsiteAdsBannersPage = React.lazy(() => import("../Page/InsiteAdsBanners"));
const ChatDesktop = React.lazy(() => import("../Component/ChatDesktop"));
const YouPage = React.lazy(loadYou);
const PopTestPage = React.lazy(() => import("../Page/PopTest"));
const VerificationPage = React.lazy(loadVerification);
const HelpPage = React.lazy(loadHelp);

/**
 * The account pages behind the You hub. Named loaders so the same import can be
 * kicked ahead of time — import() dedupes, so React.lazy picks up the warmed chunk.
 */
function loadYou() { return import("../Page/You"); }
function loadProfile() { return import("../Page/Profile"); }
function loadVerification() { return import("../Page/You/Verification"); }
function loadHelp() { return import("../Page/You/Help"); }
function loadParameters() { return import("../Page/Parameters"); }
function loadTokens() { return import("../Page/Tokens"); }

/** Warm every screen the You hub opens, so a section appears under its slide-in
    animation instead of after a Suspense loader. */
export function preloadAccountSections() {
  return Promise.allSettled([
    loadYou(), loadVerification(), loadHelp(), loadProfile(), loadParameters(), loadTokens(),
  ]);
}

function Router() {
  const isMobile = useIsMobile();
  const user = useUser();

  useEffect(() => {
    if (user?.informations?.type === "guest") {
      const search = window.location.search.substring(1).split('&');
      for (let param of search) {
        let [key, value] = param.split("=");
        if (key === "force_login_user") displayPopin()("forcelogin", { userID: value });
      };
    }
  }, []);

  // Warm the account screens for members once the app is idle — same shape as the
  // popins preload — so opening You or one of its sections never waits on a chunk.
  const isMember = !!user?.informations && user.informations.type !== "guest";
  useEffect(() => {
    if (!isMember) return;

    let idleCallback: number | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const preload = () => {
      if ("requestIdleCallback" in window) {
        idleCallback = window.requestIdleCallback(() => { void preloadAccountSections(); }, { timeout: 3000 });
      } else {
        timeout = setTimeout(() => { void preloadAccountSections(); }, 0);
      }
    };

    if (document.readyState === "complete") {
      preload();
    } else {
      window.addEventListener("load", preload, { once: true });
    }

    return () => {
      window.removeEventListener("load", preload);
      if (idleCallback !== undefined) window.cancelIdleCallback(idleCallback);
      if (timeout !== undefined) clearTimeout(timeout);
    };
  }, [isMember]);

  return (
    <Suspense fallback={<div className="bg-background flex justify-center items-center h-screen w-screen"><Loader /></div>}>
      <BrowserRouter>
        <Routes>
          {/* 2a — the rail's bottom button opens the full chat page on desktop too. */}
          <Route path="/chat" element={<Chats />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/insite-ads-banners" element={<InsiteAdsBannersPage />} />
          <Route path="/chat/:id" element={<Chat />} />
          <Route path="/member/:id" element={<Profile />} />
          <Route path="/profil/:id" element={<Profile />} />
          <Route path="/members" element={<Members />} />
          {/* 35b — /top stays a working URL: it opens Our members on the Top tab. */}
          <Route path="/top" element={<Members />} />
          <Route path="/challenge" element={<Challenge />} />
          <Route path="/challenge/:id" element={<SingleChallenge />} />
          <Route path="/search" element={<Search />} />
          {/* Developer bench: every popin behind a button. Not linked from anywhere. */}
          <Route path="/pop-test-xc2" element={<PopTestPage />} />
          <Route path="/visionnage/:id" element={<Home />} />
          <Route path="/forgotPassword" element={<ForgotPassword />} />
          <Route path="/resetPassword/:id" element={<ResetPage />} />
          <Route path="/reinitialisationmotdepasse/:id" element={<ResetPage />} />
          <Route path="/:id" element={<StaticPage />} />
          {/* 3d — guests get a You page of their own; the tab is never dead. */}
          <Route path="/you" element={<YouPage />} />
          <Route path="/" element={<Home />} />
          <Route path="*" element={<Home />} />
          <Route path="/desabonnement" element={<ManageMySub />} />

          {user?.informations?.type !== "guest" && <>
            <Route path="/webcams" element={<Webcam />} />
            <Route path="/verification" element={<VerificationPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/me" element={<Profile />} />
            <Route path="/uploader" element={<EphemeUploader />} />
            <Route path="/uploader/:id" element={<EphemeUploader />} />
            <Route path="/parameters" element={<ParametersPage />} />
            <Route path="/me/parameters" element={<ParametersPage />} />
            <Route path="/suppression" element={<SuppressionPage />} />
            <Route path="/myepheme/:id" element={<MyEpheme />} />
            <Route path="/blocked" element={<Blocked />} />
            <Route path="/tokens" element={<Tokens />} />
          </>}

        </Routes>
        <PopinsHolder />
        <ToastHolder />
        <OfferCodePrompt />
        {!isMobile.isMobile && <DeferredDesktopChat />}
        <GlobalNavigate />
      </BrowserRouter>
    </Suspense>
  );
}

function DeferredDesktopChat() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let idleCallback: number | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const loadChat = () => {
      if ("requestIdleCallback" in window) {
        idleCallback = window.requestIdleCallback(() => setReady(true), {
          timeout: 2000,
        });
      } else {
        timeout = setTimeout(() => setReady(true), 0);
      }
    };

    if (document.readyState === "complete") {
      loadChat();
    } else {
      window.addEventListener("load", loadChat, { once: true });
    }

    return () => {
      window.removeEventListener("load", loadChat);
      if (idleCallback !== undefined) {
        window.cancelIdleCallback(idleCallback);
      }
      if (timeout !== undefined) {
        clearTimeout(timeout);
      }
    };
  }, []);

  if (!ready) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <ChatDesktop />
    </Suspense>
  );
}

export const NAVIGATE: any = {
  navigate: undefined
};

function GlobalNavigate() {
  const navigate = useNavigate();
  NAVIGATE.navigate = navigate;
  return null;
}

export default Router;
